import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * SearchProvidersTable — the per-org search-credential editor (S5 of
 * `ai-docs/web-search-providers-implementation-plan.md`; plan of record
 * `ai-docs/search-providers-portal-ui-miniplan.md`).
 *
 * Same grammar as `CredentialsTable`, deliberately: one row per credential
 * (slug id · provider · masked key · label · delete), controlled by the parent,
 * ids commit-on-blur so React keys stay stable while typing.
 *
 * ⚠ MASKED-SENTINEL RULE, inherited verbatim from the LLM table:
 *   - the sentinel under an EXISTING id preserves the stored ciphertext, so a
 *     label can be edited without retyping the key;
 *   - the sentinel under a NEW id is REJECTED — a rename re-enters the key,
 *     because the SPA only ever holds the mask (the trap `CredentialsTable`
 *     documents at its `:183`).
 *
 * ⚠ NO "Testar" BUTTON HERE. `POST /api/search-providers/:orgId/test` is
 * `requirePlatformAdmin()`, so it would 403 for this component's audience — and
 * a platform-scoped capability must never enter a bundle LexIDE ships to ORG
 * admins. It lives in LexPortal. (Agreed, handshake `20260813T222927Z`.)
 *
 * ⚠ EVERY STRING IS A KEY. LexPortal mounts this bundle and omits `locale`
 * (stays en-US); LexIDE passes pt-BR. A hardcoded literal renders the wrong
 * language to one of them.
 */
import { Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { searchProviderBadge, searchProviderOptions } from "../lib/search-provider-source.js";
import { useOrgAdminT } from "../i18n/context.js";
const MASKED = "***MASKED***";
/** Providers offered even when the status has not loaded, so there is always a
 *  row to fill in. `searchProviderOptions` unions in the KEYED providers from
 *  the status payload plus whatever the row already holds.
 *
 *  ⚠ ORDER IS BEHAVIOUR: `add()` births new rows on `PROVIDER_OPTIONS[0]`, so
 *  `serper` leads deliberately — it is the platform default (⚖ A-11), while
 *  `brave` is expensive and disabled as a platform-serving provider. Re-ordering
 *  this array moves the default new-row provider; `search-provider-source.test.ts`
 *  pins it so that cannot happen silently.
 *
 *  EXPORTED so that test pins THIS array rather than a copy of it — a duplicated
 *  constant would have kept passing while this one was re-ordered. */
export const PROVIDER_OPTIONS = ["serper", "brave", "perplexity"];
/** Server gate mirrored from `org-runtime-config.ts` `SearchConfigSchema`:
 *  kebab-case slug, max 48. */
const SEARCH_ID_RE = /^[a-z][a-z0-9-]*$/;
const SEARCH_MAX_ID_LEN = 48;
const SEARCH_MAX_SECRET_LEN = 8192;
/**
 * Pre-submit check — the sibling of `findIncompleteCredential`, and it was
 * MISSING until 2026-08-15.
 *
 * Why it matters more than a nicer error: the runtime-config PATCH is ATOMIC.
 * An incomplete SEARCH row does not merely fail itself — it 400s the whole
 * request, silently discarding any LLM-credential or alias edits the operator
 * bundled into the same save. The LLM table has blocked this at the browser
 * since it shipped; the search table sent it to the server and let a
 * `VALIDATION_ERROR` come back (observed live by `search-providers`, an empty
 * secret reaching lexserver as `secret: Too small`).
 *
 * Mirrors the SERVER's rules rather than inventing softer ones, so the two
 * cannot disagree about what a valid row is. Returns a ready-to-render message
 * or null. Reuses the LLM table's validation strings deliberately — they are
 * already provider-agnostic and each names the offending id, so this adds no
 * new keys to the three-locale axis.
 */
export function findIncompleteSearchCredential(table, t) {
    for (const [id, e] of Object.entries(table)) {
        // MASKED counts as present: an existing row may be re-saved to edit only
        // its label, exactly as the LLM table allows.
        if (!e.secret?.trim())
            return t("credentials.validation.missingSecret", { id });
        if (e.secret.length > SEARCH_MAX_SECRET_LEN) {
            return t("credentials.validation.secretTooLong", { id });
        }
        if (!SEARCH_ID_RE.test(id))
            return t("credentials.validation.invalidId", { id });
        if (id.length > SEARCH_MAX_ID_LEN) {
            return t("credentials.validation.idTooLong", { id, max: SEARCH_MAX_ID_LEN });
        }
    }
    // NOT mirrored here: the ≤8-entry cap. It is unreachable by typing (only by
    // clicking Add nine times) and the server's message for it is already
    // specific, so it stays a server-side answer rather than a fifth rule that
    // could drift.
    return null;
}
export function SearchProvidersTable({ credentials, onChange, status, enabled = true, disabled = false, }) {
    const t = useOrgAdminT();
    const [reveal, setReveal] = useState({});
    const [draftIds, setDraftIds] = useState({});
    // Keep local id drafts in step when the parent replaces the table wholesale.
    useEffect(() => {
        setDraftIds({});
    }, [credentials]);
    const ids = Object.keys(credentials);
    function patch(id, next) {
        onChange({ ...credentials, [id]: { ...credentials[id], ...next } });
    }
    function remove(id) {
        const next = { ...credentials };
        delete next[id];
        onChange(next);
    }
    function add() {
        let n = 1;
        while (credentials[`search-${n}`])
            n += 1;
        onChange({
            ...credentials,
            [`search-${n}`]: { provider: PROVIDER_OPTIONS[0], kind: "api-key", secret: "" },
        });
    }
    function commitId(oldId) {
        const draft = (draftIds[oldId] ?? "").trim();
        if (!draft || draft === oldId)
            return;
        if (credentials[draft])
            return; // collision — keep the old id
        const entry = credentials[oldId];
        const next = {};
        for (const [k, v] of Object.entries(credentials)) {
            // A rename cannot carry a masked secret across — the server would see
            // the sentinel under an id it has never stored and reject it. Clear it
            // so the operator is asked for the key rather than silently failing.
            if (k === oldId)
                next[draft] = { ...entry, secret: entry.secret === MASKED ? "" : entry.secret };
            else
                next[k] = v;
        }
        onChange(next);
    }
    /** Badge for a provider, from the INDEPENDENT status facts. Undefined while
     *  the status has not loaded — the caller renders nothing rather than a
     *  guess, because "not yet read" is not "not configured". */
    function badgeFor(provider) {
        if (!status)
            return undefined;
        const row = status.find((r) => r.provider === provider);
        if (!row)
            return undefined;
        return searchProviderBadge({
            orgEntry: row.orgEntry,
            platformFallback: row.platformFallback,
            // The server's verdict outranks `platformFallback` on the platform half
            // (⚖ ruling 2026-08-15) — brave is STORED but disabled from serving.
            effectiveSource: row.effectiveSource,
            enabled,
        });
    }
    function badgeLabel(kind) {
        if (kind === "org-key")
            return t("searchProviders.srcOrg");
        if (kind === "platform-default")
            return t("searchProviders.srcPlatform");
        return t("searchProviders.srcNone");
    }
    return (_jsxs("div", { className: "space-y-3", "data-testid": "search-providers-table", children: [_jsxs("div", { children: [_jsx("h3", { className: "text-sm font-medium", children: t("searchProviders.title") }), _jsx("p", { className: "mt-1 text-xs text-slate-500", children: t("searchProviders.help") })] }), _jsx("div", { className: "space-y-2", children: ids.map((id) => {
                    const entry = credentials[id];
                    const badge = badgeFor(entry.provider);
                    return (_jsxs("div", { className: "rounded-md border border-slate-200 p-3 dark:border-slate-700", "data-testid": `search-provider-row-${id}`, children: [_jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx("input", { value: draftIds[id] ?? id, onChange: (e) => setDraftIds((d) => ({ ...d, [id]: e.target.value })), onBlur: () => commitId(id), disabled: disabled, className: "w-32 rounded border border-slate-300 px-2 py-1 font-mono text-xs dark:border-slate-600 dark:bg-slate-900", "data-testid": `search-provider-id-${id}` }), _jsx("select", { value: entry.provider, onChange: (e) => patch(id, { provider: e.target.value }), disabled: disabled, className: "rounded border border-slate-300 px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-900", "data-testid": `search-provider-select-${id}`, children: searchProviderOptions({
                                            seeds: PROVIDER_OPTIONS,
                                            status,
                                            current: entry.provider,
                                        }).map((p) => (_jsx("option", { value: p, children: p }, p))) }), badge && (_jsxs("span", { className: "rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600 dark:bg-slate-800 dark:text-slate-300", "data-testid": `search-provider-badge-${id}`, children: [badgeLabel(badge.kind), badge.disabled && ` — ${t("searchProviders.disabledNote")}`] })), _jsx("button", { type: "button", onClick: () => remove(id), disabled: disabled, className: "ml-auto rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20", "data-testid": `search-provider-delete-${id}`, children: _jsx(Trash2, { className: "h-3.5 w-3.5" }) })] }), _jsxs("div", { className: "mt-2 flex items-center gap-2", children: [_jsx("input", { type: reveal[id] ? "text" : "password", 
                                        // A provider SECRET, not a login password. Chrome offers to fill a
                                        // saved site password into any input[type=password] it associates
                                        // with the origin — measured live on LexIDE 2026-09-13, where a
                                        // stored Lexiun password landed in the bot-token field masked, so
                                        // the operator saw only dots. "off" is unreliable on password-typed
                                        // inputs (it is honoured on the text input at
                                        // RuntimeConfigPage.tsx:297); "new-password" is what suppresses the
                                        // saved-password fill.
                                        autoComplete: "new-password", value: entry.secret, onChange: (e) => patch(id, { secret: e.target.value }), disabled: disabled, className: "flex-1 rounded border border-slate-300 px-2 py-1 font-mono text-xs dark:border-slate-600 dark:bg-slate-900", "data-testid": `search-provider-secret-${id}` }), _jsx("button", { type: "button", onClick: () => setReveal((r) => ({ ...r, [id]: !r[id] })), className: "rounded p-1 text-slate-500", children: reveal[id] ? _jsx(EyeOff, { className: "h-3.5 w-3.5" }) : _jsx(Eye, { className: "h-3.5 w-3.5" }) })] })] }, id));
                }) }), _jsxs("button", { type: "button", onClick: add, disabled: disabled, className: "inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs dark:border-slate-600", "data-testid": "search-provider-add", children: [_jsx(Plus, { className: "h-3.5 w-3.5" }), " ", t("credentials.addCredential")] })] }));
}
//# sourceMappingURL=SearchProvidersTable.js.map